// Shuffle array in place using Fisher-Yates algorithm
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

// Normalize key to lowercase for comparison
const normalizeKey = (key) => (key || '').toString().trim().toLowerCase();

// Extract option key and separator from "A) text", "A. text", "A text", etc.
const getKeyAndRest = (optionText) => {
  const match = optionText.match(/^\s*([^)\s.]+)([)\s.])?\s*(.*)$/);
  if (match) {
    const key = match[1];
    const separator = match[2] || '';
    const rest = match[3];
    return { key, separator, rest };
  } else {
    return { key: optionText[0] || '', separator: '', rest: optionText.slice(1) };
  }
};

// Extract option key from "A) text", "ii) text", etc.
export const getOptionKey = (optionText) => {
  const { key } = getKeyAndRest(optionText);
  return normalizeKey(key);
};

// Convert index to corresponding key (A, B, C, D or a, b, c, d or i, ii, iii, iv)
const indexToKey = (index, originalCorrect) => {
  const isLower = originalCorrect === originalCorrect.toLowerCase();
  const isRoman = /^[iv]+$/i.test(originalCorrect);
  if (isLower && !isRoman) {
    return String.fromCharCode(97 + index); // a, b, c, d
  } else if (isRoman) {
    const romans = ['i', 'ii', 'iii', 'iv'];
    const roman = romans[index];
    return isLower ? roman : roman.toUpperCase();
  } else {
    return String.fromCharCode(65 + index); // A, B, C, D
  }
};

// Shuffle options within each question (questions stay in source order)
// and update correct answers to match the new option positions.
export const shuffleQuestions = (questions) => {
  // Preserve the original question order; only clone the array and each
  // question so we don't mutate the caller's data.
  const processedQuestions = questions.map(q => ({ ...q }));

  // For each question, shuffle options and update correct answer
  processedQuestions.forEach(q => {
    if (q.options && q.options.length > 0) {
      // Find the original correct option key
      const originalCorrectNormalized = normalizeKey(q.correct);

      // Shuffle the options array
      const shuffledOptions = shuffleArray([...q.options]);

      // Find the new index of the correct option after shuffling
      let newCorrectIndex = -1;
      for (let i = 0; i < shuffledOptions.length; i++) {
        if (normalizeKey(getOptionKey(shuffledOptions[i])) === originalCorrectNormalized) {
          newCorrectIndex = i;
          break;
        }
      }

      // Update option texts with new prefixes
      shuffledOptions.forEach((opt, idx) => {
        const newKey = indexToKey(idx, q.correct);
        const { separator, rest } = getKeyAndRest(opt);
        shuffledOptions[idx] = newKey + separator + ' ' + rest;
      });

      // Update the correct answer to the new key
      q.correct = indexToKey(newCorrectIndex, q.correct);
      q.options = shuffledOptions;
    }
  });

  return processedQuestions;
};

export const calculateScore = (questions, answers) => {
    let correct = 0, wrong = 0, unanswered = 0;
    questions.forEach(q => {
      if (answers[q.id]) {
        // Normalize both user answer and correct answer to lowercase for comparison
        const normalizedUserAnswer = (answers[q.id] || '').toString().trim().toLowerCase();
        const normalizedCorrect = (q.correct || '').toString().trim().toLowerCase();
        if (normalizedUserAnswer === normalizedCorrect) correct++;
        else wrong++;
      } else unanswered++;
    });
    const totalAttempted = correct + wrong;
    const skipped = unanswered;
    const totalMarks = correct - (wrong / 3);
    const percentage = questions.length > 0 ? ((totalMarks / questions.length) * 100).toFixed(2) : '0.00';
    return { correct, wrong, skipped, totalAttempted, totalMarks, percentage };
  };
